import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, X, Star,
  Bug, Lightbulb, BarChart3, Settings as SettingsIcon, ChevronRight, Sun, Moon, Share2, Crown, ScrollText, Link2,
  ExternalLink, Heart, Users,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { planLabel } from "@/lib/subscription";
import ReleaseNotes from "@/components/ReleaseNotes";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { getReferralCode, getSnaggedifyStats, getUserHistory } from "@/lib/api";
import { getStoredTheme, setTheme } from "@/lib/theme";
import { toast } from "@/components/ui/sonner";
import { ConfirmDeleteDialog } from "@/components/PriceGuards";
import SubmitAlternativeWebsite from "@/components/SubmitAlternativeWebsite";
import VaultGridModal from "@/components/VaultGridModal";
import { guessManufacturer } from "@/features/snaggedify/dealFeedData";

const TALLY_REVIEW_URL = "https://tally.so/r/kdgXVr";
const TALLY_BUG_URL = "https://tally.so/r/kdg4ao";
const APP_VERSION_LABEL = "Snaggedify v1.0 — Early Access (Alpha)";

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// Section 8 — History tab event → icon/label mapping (checked links, outbound clicks,
// saved cards — the 3 real activity sources now that Vault persists server-side).
const HISTORY_META = {
  checked: { Icon: Link2, cls: "bg-sky-500/10 text-sky-500", label: (ev) => `Checked ${ev.title || ev.domain || "a link"}` },
  outbound_click: { Icon: ExternalLink, cls: "bg-orange-500/10 text-orange-500", label: (ev) => `Snagged a deal at ${ev.merchant || "a seller"}` },
  saved: { Icon: Heart, cls: "bg-rose-500/10 text-rose-500", label: (ev) => `Saved ${ev.title || "a card"}` },
};

// Glassmorphic dark right-side Profile Drawer — houses every utility so the home
// screen stays a single clean paste-to-verdict surface.
export default function ProfileModal({ open, onClose, onReplay, onReplayTutorial, onOpenShop, onOpenUpgrade, onOpenSettings, onOpenDash, onOpenInspect, onOpenCreateCard, onOpenPdp }) {
  const { user, logout, deleteHistory } = useAuth();
  const navigate = useNavigate();
  const [confirmDel, setConfirmDel] = useState(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [showVault, setShowVault] = useState(false);
  const [shown, setShown] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [refCode, setRefCode] = useState("");
  const [history, setHistory] = useState(null);
  const [theme, setThemeState] = useState(getStoredTheme());
  const [avatarError, setAvatarError] = useState(false);
  useBodyScrollLock(open);

  const toggleTheme = (next) => setThemeState(setTheme(next));

  const shareApp = async () => {
    const inviteUrl = refCode ? `${window.location.origin}/?ref=${refCode}` : window.location.origin;
    const data = {
      title: "Snaggedify",
      text: "Scroll, Discover, and Check any deal for the real price with Snaggedify by Cheapify. Sign up with my link and we both get +5 Bonus Credits!",
      url: inviteUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else {
        await navigator.clipboard.writeText(data.url);
        toast.success("Invite link copied to clipboard — share it anywhere!");
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        try { await navigator.clipboard.writeText(data.url); toast.success("Invite link copied to clipboard!"); }
        catch { toast.error("Couldn't share right now."); }
      }
    }
  };

  useEffect(() => {
    if (open) { const t = setTimeout(() => setShown(true), 10); return () => clearTimeout(t); }
    setShown(false);
    setActiveTab("overview");
  }, [open]);

  useEffect(() => {
    if (open && user && !user.is_guest) {
      getSnaggedifyStats().then(setStats).catch((e) => console.error("snaggedify stats failed (ignored):", e));
      getReferralCode().then((r) => setRefCode(r.ref_code)).catch((e) => console.error("referral code fetch failed (ignored):", e));
    }
  }, [open, user]);

  useEffect(() => {
    if (open && activeTab === "history" && user && !user.is_guest) {
      getUserHistory().then((r) => setHistory(r.events || [])).catch(() => setHistory([]));
    }
  }, [open, activeTab, user]);

  if (!open) return null;

  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  const confirmDeleteRow = () => {
    if (confirmDel?.txId) deleteHistory(confirmDel.txId);
    setConfirmDel(null);
  };
  const close = () => { setShown(false); setTimeout(() => onClose?.(), 200); };
  const handleLogout = async () => { await logout(); onClose?.(); navigate("/"); };
  const go = (fn) => { fn?.(); close(); };

  // Section: History → PDP — tapping any history entry reopens its full standardized
  // PDP preview (same modal as Scroll/Discover/Check), reconstructed from whatever
  // fields that event type persisted server-side.
  const openHistoryPdp = (ev) => {
    if (ev.type === "checked") {
      onOpenPdp?.({
        id: ev.url || ev.domain, title: ev.title || ev.domain || "Checked Link", price: ev.price ?? 0, msrp: ev.msrp ?? ev.price ?? 0,
        image: ev.image || "", reseller: ev.domain || "", manufacturer: guessManufacturer(ev.title || ev.domain || ""),
        verdict: (ev.verdict || "").replace("VERDICT_LEGIT", "VERIFIED_LEGIT").replace("VERDICT_", ""),
        outboundUrl: ev.url, fulfillment: "ONLINE_SHIPPING",
      });
    } else if (ev.type === "saved") {
      onOpenPdp?.({
        id: ev.deal_id, title: ev.title, price: ev.price ?? 0, msrp: ev.msrp ?? ev.price ?? 0,
        image: ev.image || "", reseller: ev.reseller || "", manufacturer: guessManufacturer(ev.title || ""),
        verdict: ev.verdict, outboundUrl: ev.outbound_url, fulfillment: ev.fulfillment_mode || "ONLINE_SHIPPING",
      });
    } else if (ev.type === "outbound_click") {
      onOpenPdp?.({
        id: ev.item_id || ev.merchant, title: ev.item_title || ev.merchant || "Snagged Deal", price: ev.price ?? 0, msrp: ev.price ?? 0,
        image: "", reseller: ev.merchant || "", manufacturer: guessManufacturer(ev.item_title || ""),
        verdict: "VERIFIED_LEGIT", outboundUrl: ev.raw_url, fulfillment: "ONLINE_SHIPPING",
      });
    }
  };

  const MENU = [
    { icon: Crown, label: "Upgrade Plan", desc: "Unlock more Snaggedify features", tid: "drawer-upgrade", onClick: () => go(onOpenUpgrade) },
    { icon: Link2, label: "Check a Link", desc: "Verify a deal link manually", tid: "drawer-inspect-link", onClick: () => go(onOpenInspect) },
    { icon: ScrollText, label: "Update Logs", desc: "See what's new in Snaggedify", tid: "drawer-release-notes", onClick: () => setShowReleaseNotes(true) },
    { icon: Star, label: "Leave a Review", desc: "Tell us what you love — new tab", tid: "drawer-review", href: TALLY_REVIEW_URL },
    { icon: Bug, label: "Report a Bug", desc: "Spotted something off? — new tab", tid: "drawer-bug", href: TALLY_BUG_URL },
    { icon: SettingsIcon, label: "Settings", desc: "Location, notifications & more", tid: "drawer-settings", onClick: () => go(onOpenSettings) },
  ];
  if (user?.is_admin) {
    MENU.push({ icon: BarChart3, label: "Admin Metrics", desc: "Owner analytics dashboard", tid: "drawer-admin", onClick: () => go(() => navigate("/admin")) });
  }

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="profile-drawer">
      <div className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-200 ${shown ? "opacity-100" : "opacity-0"}`} onClick={close} />
      <div
        className={`relative flex h-full w-full max-w-sm flex-col border-l border-slate-200 bg-white/95 dark:border-white/[0.08] dark:bg-charcoal/95 shadow-2xl backdrop-blur-3xl transition-transform duration-300 ease-out pt-[env(safe-area-inset-top)] ${shown ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex items-center gap-4 border-b border-slate-200 dark:border-white/10 px-6 py-5">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-brand-yellow to-brand-orange text-black">
            {user?.picture && !avatarError ? <img src={user.picture} alt={user.name} onError={() => setAvatarError(true)} className="h-full w-full object-cover" /> : <span className="text-xl font-black">{initial}</span>}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-black tracking-tight text-slate-900 dark:text-white" data-testid="profile-name">{user?.name || "Member"}</h2>
            <p className="truncate text-sm text-slate-500 dark:text-zinc-400" data-testid="profile-email">{user?.email}</p>
            <span className="mt-1.5 inline-flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-full border border-brand-yellow/40 bg-brand-yellow/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-brand-orange" data-testid="profile-plan-badge">
                <Crown className="h-3 w-3" /> {planLabel(user)}
              </span>
              {user?.comp_vip && (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-emerald-400" data-testid="profile-comp-vip-badge">
                  <Crown className="h-3 w-3" /> VIP Access · Lifetime
                </span>
              )}
            </span>
          </div>
          <button onClick={close} data-testid="profile-drawer-close" aria-label="Close" className="shrink-0 rounded-full p-2 text-slate-400 dark:text-zinc-400 transition-colors hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-700 dark:hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {/* Section 8 — Overview / History tab switcher */}
        <div className="flex gap-1 border-b border-slate-200 px-6 pt-3 dark:border-white/10" data-testid="profile-tab-switcher">
          {[["overview", "Overview"], ["history", "History"]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              data-testid={`profile-tab-${key}`}
              className={`rounded-full px-4 py-2 text-xs font-black uppercase tracking-wide transition-colors ${activeTab === key ? "bg-slate-900 text-white dark:bg-white dark:text-slate-950" : "text-slate-400 hover:text-slate-700 dark:text-zinc-500 dark:hover:text-white"}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-5">
          {activeTab === "history" ? (
            <div className="space-y-2" data-testid="history-tab-content">
              <button onClick={() => setShowVault(true)} data-testid="open-vault-grid-btn" className="flex w-full items-center justify-between gap-3 rounded-2xl border border-brand-yellow/40 bg-brand-yellow/10 px-4 py-3 text-left transition-colors hover:bg-brand-yellow/20">
                <span className="flex items-center gap-2 text-sm font-black text-brand-orange"><Heart className="h-4 w-4 fill-current" /> View Vault</span>
                <ChevronRight className="h-4 w-4 text-brand-orange" />
              </button>
              {history === null ? (
                <p className="py-8 text-center text-xs font-bold text-slate-400 dark:text-zinc-500">Loading…</p>
              ) : history.length === 0 ? (
                <p className="py-8 text-center text-xs font-bold text-slate-400 dark:text-zinc-500" data-testid="history-empty">No activity yet — check a link, save a deal, or snag one to see it here.</p>
              ) : (
                history.map((ev, i) => {
                  const meta = HISTORY_META[ev.type] || HISTORY_META.checked;
                  return (
                    <button
                      key={i}
                      onClick={() => openHistoryPdp(ev)}
                      data-testid={`history-item-${i}`}
                      className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-left transition-colors hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10"
                    >
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${meta.cls}`}><meta.Icon className="h-3.5 w-3.5" /></span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-bold text-slate-900 dark:text-white">{meta.label(ev)}</p>
                        <p className="text-[10px] text-slate-400 dark:text-zinc-500">{fmtTime(ev.at)}</p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          ) : (
            <>
              {/* Appearance — Light / Dark theme toggle (persists across sessions) */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50 dark:border-white/[0.08] dark:bg-white/5 p-3 backdrop-blur-xl" data-testid="theme-toggle-card">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-900 dark:text-white">Appearance</p>
                    <p className="text-xs text-slate-500 dark:text-zinc-400">Studio dark or light</p>
                  </div>
                  <div className="inline-flex shrink-0 rounded-full border border-slate-200 bg-slate-100 dark:border-white/[0.08] dark:bg-black/30 p-0.5" role="group" aria-label="Theme">
                    <button
                      onClick={() => toggleTheme("dark")}
                      data-testid="theme-toggle-dark"
                      aria-pressed={theme === "dark"}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${theme === "dark" ? "bg-gradient-to-r from-brand-yellow to-brand-orange text-black" : "text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white"}`}
                    >
                      <Moon className="h-3.5 w-3.5" /> Dark
                    </button>
                    <button
                      onClick={() => toggleTheme("light")}
                      data-testid="theme-toggle-light"
                      aria-pressed={theme === "light"}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${theme === "light" ? "bg-slate-900 text-white dark:bg-white dark:text-black" : "text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white"}`}
                    >
                      <Sun className="h-3.5 w-3.5" /> Light
                    </button>
                  </div>
                </div>
              </div>

              {/* Section 8 — Your Stats: real Checked/Saved/Snagged/Savings (replaces the
                  legacy pack-opening "Packs Opened / Cards Revealed" text) */}
              {stats && (
                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/5 p-4" data-testid="profile-stats-card">
                  <p className="text-[10px] font-black uppercase tracking-[0.16em] text-brand-orange">Your Stats</p>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-lg font-black tabular-nums text-slate-900 dark:text-white" data-testid="stat-checked">{stats.checked_count}</p><p className="text-[9px] uppercase tracking-wide text-slate-400">Deals Checked</p></div>
                    <div><p className="text-lg font-black tabular-nums text-slate-900 dark:text-white" data-testid="stat-saved">{stats.saved_count}</p><p className="text-[9px] uppercase tracking-wide text-slate-400">Vault Saves</p></div>
                    <div><p className="text-lg font-black tabular-nums text-emerald-500" data-testid="stat-savings">${stats.est_savings}</p><p className="text-[9px] uppercase tracking-wide text-slate-400">Est. Savings</p></div>
                  </div>
                </div>
              )}

              {/* Section 7 — Invite & Earn: this user's own referral code + one-tap share */}
              {refCode && (
                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/5 p-4" data-testid="invite-earn-card">
                  <p className="text-[10px] font-black uppercase tracking-[0.16em] text-brand-orange">Invite & Earn</p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-zinc-400">Share your code — you both get +5 Bonus Credits when they join.</p>
                  <div className="mt-2 flex items-center justify-between gap-2 rounded-xl border border-dashed border-brand-orange/40 bg-white px-3 py-2 dark:bg-black/20">
                    <span className="font-mono text-sm font-black tracking-wide text-slate-900 dark:text-white" data-testid="referral-code">{refCode}</span>
                    <button onClick={shareApp} data-testid="invite-share-btn" className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-[11px] font-black uppercase text-white dark:bg-white dark:text-slate-950">
                      <Share2 className="h-3 w-3" /> Invite
                    </button>
                  </div>
                  <div className="mt-2 flex items-center gap-2 rounded-xl bg-white px-3 py-2 dark:bg-black/20" data-testid="friends-invited-tile">
                    <Users className="h-3.5 w-3.5 text-brand-orange" />
                    <span className="text-xs font-bold text-slate-700 dark:text-zinc-300"><span className="font-black text-slate-900 dark:text-white" data-testid="friends-invited-count">{stats?.invited_count ?? 0}</span> Friends Invited</span>
                  </div>
                </div>
              )}

              {/* Utility menu */}
              <div className="mt-3 space-y-2" data-testid="drawer-menu">
                {MENU.map((m) => {
                  const Inner = (
                    <>
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 dark:bg-white/10"><m.icon className="h-4 w-4 text-brand-orange dark:text-brand-yellow" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-bold text-slate-900 dark:text-white">{m.label}</span>
                        <span className="block text-xs text-slate-500 dark:text-zinc-400">{m.desc}</span>
                      </span>
                      <ChevronRight className="h-5 w-5 shrink-0 text-slate-400 dark:text-zinc-500" />
                    </>
                  );
                  const cls = "flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/5 px-4 py-3.5 text-left backdrop-blur-xl transition-colors hover:bg-slate-100 dark:hover:bg-white/10";
                  return m.href ? (
                    <a key={m.tid} href={m.href} target="_blank" rel="noopener noreferrer" data-testid={m.tid} className={cls}>{Inner}</a>
                  ) : (
                    <button key={m.tid} onClick={m.onClick} data-testid={m.tid} className={cls}>{Inner}</button>
                  );
                })}
              </div>

              <button onClick={handleLogout} data-testid="logout-btn" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-500/30 bg-rose-500/10 py-3.5 text-sm font-bold uppercase tracking-[0.08em] text-rose-400 transition-colors hover:bg-rose-500/20">
                <LogOut className="h-4 w-4" /> Logout
              </button>

              <p className="mt-4 text-center text-[10px] font-bold uppercase tracking-[0.15em] text-slate-300 dark:text-zinc-600" data-testid="app-version-label">{APP_VERSION_LABEL}</p>
            </>
          )}
        </div>
      </div>

      <ConfirmDeleteDialog open={!!confirmDel} name={confirmDel?.name} onCancel={() => setConfirmDel(null)} onConfirm={confirmDeleteRow} />
      <SubmitAlternativeWebsite open={showSuggest} onClose={() => setShowSuggest(false)} />
      <VaultGridModal open={showVault} onClose={() => setShowVault(false)} />
      {showReleaseNotes && <ReleaseNotes onClose={() => setShowReleaseNotes(false)} />}
    </div>
  );
}
